// VersionUpdateBanner — proactive "a new version is available" prompt.
//
// Complements the existing *reactive* deploy-resilience layers (lazyWithRetry
// retries, chunkErrorHandler auto-reload, ErrorBoundary refresh UI), which only
// fire once the user happens to navigate to a route whose chunk 404s after a
// deploy. This banner instead *polls* for a new build, so a user sitting on a
// long-lived page (dispatch board, load detail) is told to refresh before they
// hit a stale-chunk error.
//
// How it detects a deploy: base.html seeds `window.__BUILD_HASH__` with the
// webpack build hash the page was rendered with. /version/ returns the hash the
// server is currently on. After a deploy + gunicorn reload those diverge, which
// is the signal. When no baseline hash exists (HMR dev-server serves bundles
// in-memory, so build-hash.json is absent → empty string) the check disables
// itself, so this never fires during local development.
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import cacheBustReload from '@shared/utils/cacheBustReload';

const VERSION_URL = '/version/';
const POLL_INTERVAL_MS = 3 * 60 * 1000; // re-check every 3 minutes
const MIN_CHECK_GAP_MS = 30 * 1000;     // throttle focus/visibility-driven checks

/**
 * Polls the server build hash and returns true once it differs from the hash
 * this page was served with. Polling stops as soon as a difference is found
 * (a deploy doesn't get un-deployed) and while the tab is hidden.
 */
function useVersionAvailable() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    const loadedHash =
      typeof window !== 'undefined' ? window.__BUILD_HASH__ : '';
    // No baseline to compare against (dev/HMR, or build-hash.json missing).
    if (!loadedHash) return undefined;

    let cancelled = false;
    let intervalId;

    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };

    const stop = () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };

    async function check() {
      // Skip while backgrounded (visibilitychange re-checks on return) and
      // throttle bursty focus/visibility events.
      if (cancelled || document.hidden) return;
      const now = Date.now();
      if (now - lastCheckRef.current < MIN_CHECK_GAP_MS) return;
      lastCheckRef.current = now;
      try {
        const res = await fetch(VERSION_URL, {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return; // transient (e.g. brief 502 mid-deploy) — retry later
        const data = await res.json();
        if (!cancelled && data && data.hash && data.hash !== loadedHash) {
          setUpdateAvailable(true);
          stop();
        }
      } catch {
        // Network/parse blip — ignore and try again on the next tick.
      }
    }

    // The page was just served, so its hash is current — don't check on mount.
    // Start on the interval, and opportunistically when the tab regains focus.
    intervalId = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return stop;
  }, []);

  return updateAvailable;
}

export default function VersionUpdateBanner() {
  const updateAvailable = useVersionAvailable();
  const [dismissed, setDismissed] = useState(false);

  return (
    <Snackbar
      open={updateAvailable && !dismissed}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: 1500 }}
    >
      <Alert
        severity="info"
        variant="outlined"
        // Outlined keeps it light, but outlined alerts are transparent — give it
        // an opaque paper background + a soft shadow so it stays readable as a
        // floating card over page content rather than a heavy filled bar.
        sx={{ alignItems: 'center', bgcolor: 'background.paper', boxShadow: 3 }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button color="inherit" size="small" onClick={() => setDismissed(true)}>
              Dismiss
            </Button>
            <Button
              color="primary"
              size="small"
              variant="contained"
              onClick={cacheBustReload}
            >
              Reload
            </Button>
          </Box>
        }
      >
        A new version is available. Reload to get the latest.
      </Alert>
    </Snackbar>
  );
}
